import { Component, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { Call, Speaker } from '../../calls.service'
import { stringToColor } from '../../utils'
import { SpeakerPipe } from '../../speaker.pipe'
import { MatIcon } from '@angular/material/icon'
import { TranslatePipe } from '@ngx-translate/core'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete'
import { roundToTwoDecimals, SpeakerSlot } from '../x-ray-speaker-generation'
import { AutofocusDirective } from '../../auto-focus.directive'
import { MatTooltip } from '@angular/material/tooltip'

@Component({
  selector: 'app-speaker-x-ray-item',
  standalone: true,
  imports: [
    MatIcon,
    SpeakerPipe,
    TranslatePipe,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatAutocompleteModule,
    MatAutocomplete,
    AutofocusDirective,
    MatTooltip,
  ],
  providers: [SpeakerPipe],
  templateUrl: './speaker-x-ray-item.component.html',
  styleUrl: './speaker-x-ray-item.component.scss',
})
export class SpeakerXRayItemComponent implements OnChanges, OnInit {
  @Input({ required: true })
  speakerSlot!: SpeakerSlot

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canEdit!: boolean

  @Input({ required: true })
  isActive!: boolean

  @Input({ required: true })
  isLabelingModeFocusedOnSpeaker!: boolean

  @Output()
  splitSpeakers = new EventEmitter<void>()

  @Output()
  selectTime = new EventEmitter<number>()

  @Output()
  updateSpeakerName = new EventEmitter<string>()

  @Output()
  skipLabelingSpeaker = new EventEmitter<void>()

  isEditing = false
  name: string = ''

  speakerPipe = inject(SpeakerPipe)

  filteredAttendeeNames: string[] = []

  filterAttendeeNames(): void {
    const filterValue = this.name.toLowerCase()
    this.filteredAttendeeNames = this.call.attendeeNames.filter((option) => option.toLowerCase().includes(filterValue))
  }

  get isNameValid(): boolean {
    const isNewNameSameAsOriginal = this.name === this.displayName
    return this.name.trim().length > 0 && !isNewNameSameAsOriginal
  }

  get isPhysicalSpeaker(): boolean {
    return this.call.userSpeakerMapping !== null && this.call.userSpeakerMapping[this.speakerSlot.speaker.speakerTag] === this.speakerSlot.speaker.speakerTag
  }

  ngOnInit(): void {
    if (!this.isPhysicalSpeaker) {
      this.name = this.speakerPipe.transform(this.speakerSlot.speaker, this.call.userSpeakerMapping!)
    }
    this.isEditing = this.isLabelingModeFocusedOnSpeaker
    this.filteredAttendeeNames = [...this.call.attendeeNames]
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isLabelingModeFocusedOnSpeaker'] && !changes['isLabelingModeFocusedOnSpeaker'].firstChange) {
      this.isEditing = changes['isLabelingModeFocusedOnSpeaker'].currentValue
    }
  }

  hasMultipleSpeakers(slot: SpeakerSlot): boolean {
    return Array.from(new Set(slot.segments.map((segment) => segment.originalTag))).length > 1
  }

  handleSplitSpeakers() {
    this.splitSpeakers.emit()
  }

  speakerNameToColor(name: string): string {
    return stringToColor(name)
  }

  chooseTimeBasedOnClickCoordinates(event: MouseEvent) {
    const container = document.querySelector(`#speaker-x-ray-${this.speakerSlot.speaker.speakerTag}`)!
    const boundingClientRect = container.getBoundingClientRect()
    const percentage = roundToTwoDecimals((event.x - boundingClientRect.x) / container.clientWidth)
    const time = this.call.duration! * percentage
    this.selectTime.emit(time)
  }

  save() {
    if (!this.isNameValid) {
      return
    }
    this.updateSpeakerName.emit(this.name)
    this.isEditing = false
  }
  skipLabeling() {
    this.skipLabelingSpeaker.emit()
  }

  get displayName(): string {
    return this.speakerPipe.transform(this.speakerSlot.speaker, this.call.userSpeakerMapping!)
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (!this.isEditing) {
      return
    }

    if (event.key == 'Enter') {
      this.save()
    } else if (event.key == 'Escape') {
      this.isEditing = false
    }
  }

  enableEditing() {
    if (!this.isPhysicalSpeaker) {
      this.name = this.displayName
    } else {
      this.name = ''
    }
    this.isEditing = true
  }

  get displaySpeakerDecomposition(): boolean {
    // return ![CallType.VIDEO_UPLOAD, CallType.AUDIO_UPLOAD].includes(this.call.type) && this.call.physicalSpeakersExist && this.canEdit
    return false
  }

  isAutolabelled(speaker: Speaker): boolean {
    if (this.call.automatedSpeakerMapping === null) {
      return false
    }
    const userMappingValue = this.call.userSpeakerMapping?.[speaker.speakerTag]
    const automatedMappingValue = this.call.automatedSpeakerMapping[speaker.speakerTag]
    return automatedMappingValue !== undefined && userMappingValue === automatedMappingValue
  }
}
